import ListItem from "@/components/ListItem/ListItem";
import { ObjectHeadingWrapper } from "@/features/person/components/PersonObjectHeading";
import { getCurrentPrimaryEmployment } from "@/features/person/utils/utils";
import { useNavigate } from "@/router";
import { BackofficePerson } from "@/types";
import SubSectionHeading from "@/components/SubSectionHeading";
import { openPersonProfileInNewTab } from "./personSideSheetActions";
import {
  phoneOptions,
  emailOptions,
  addressOptions,
  employmentOptions,
  personalOptions,
  facilityOptions,
  jobApplicationOptions,
  programOptions,
  groupOptions
} from "./utils";

interface PersonSideSheetContentProps {
  person: BackofficePerson;
  pgaId: string;
  showDefaultSections?: boolean;
  showPersonalDetails?: boolean;
  showEmployment?: boolean;
  showFacility?: boolean;
  showJobApplications?: boolean;
  alternateEmail?: string;
  alternatePhone?: string;
  universityName?: string;
  showContactSections?: boolean;
  showGroupsSection?: boolean;
  showProfileActions?: boolean;
  children?: React.ReactNode;
}

export default function PersonSideSheetContent({
  person,
  pgaId,
  showDefaultSections = true,
  showPersonalDetails = false,
  showEmployment = false,
  showFacility = false,
  showJobApplications = false,
  alternateEmail,
  alternatePhone,
  universityName,
  showContactSections = true,
  showGroupsSection = true,
  showProfileActions = true,
  children
}: PersonSideSheetContentProps) {
  const navigate = useNavigate();
  const effectiveUniversityName = universityName || person?.university?.name;
  const primaryEmployment = getCurrentPrimaryEmployment(person.employments);

  const sections = [
    ...(person && programOptions(person, effectiveUniversityName).length > 0
      ? [
          {
            heading: "Program",
            options: programOptions(person, effectiveUniversityName)
          }
        ]
      : person && showGroupsSection && groupOptions(person).length > 0
        ? [
            {
              heading: "Groups",
              options: groupOptions(person)
            }
          ]
        : []),
    ...(showContactSections
      ? [
          {
            heading: "Phone",
            options: phoneOptions(person?.phoneNumbers, alternatePhone)
          },
          {
            heading: "Email",
            options: emailOptions(person?.email, alternateEmail)
          },
          {
            heading: "Addresses",
            options: addressOptions(person?.addresses)
          }
        ]
      : []),
    ...(person && showPersonalDetails && personalOptions(person).length > 0
      ? [
          {
            heading: "Personal",
            options: personalOptions(person)
          }
        ]
      : []),
    ...(person && showJobApplications && jobApplicationOptions(person, navigate).length > 0
      ? [
          {
            heading: "Job Application History",
            options: jobApplicationOptions(person, navigate),
            headingAction: pgaId ? () => window.open(`/people/${pgaId}/career/job-applications`, "_blank") : undefined
          }
        ]
      : []),
    ...(person && showEmployment && employmentOptions(person.employments, navigate, pgaId, 3).length > 0
      ? [
          {
            heading: "Employment",
            options: employmentOptions(person.employments, navigate, pgaId, 3),
            headingAction: pgaId ? () => window.open(`/people/${pgaId}/career/employment`, "_blank") : undefined
          }
        ]
      : []),
    ...(person && showFacility && facilityOptions(primaryEmployment, navigate).length > 0
      ? [
          {
            heading: "Facility",
            options: facilityOptions(primaryEmployment, navigate)
          }
        ]
      : [])
  ];

  const allSections = showDefaultSections ? sections : [];

  return (
    <div>
      <ObjectHeadingWrapper
        personData={{ person }}
        pgaId={pgaId}
        refreshLayout
        compact
        sideSheet
        showEmploymentInHeader={!showEmployment}
        showProfileLink={showProfileActions}
        onOpenProfile={() => openPersonProfileInNewTab(pgaId)}
      />

      {allSections.map((section: any, index: number) => (
        <div key={index} className="flex flex-col gap-2 items-start mt-8">
          <SubSectionHeading onClick={section.headingAction}>{section.heading}</SubSectionHeading>
          <div className="space-y-4 w-full">
            {section.options.map((option: any, optionIndex: number) => (
              <ListItem key={optionIndex} {...option} iconBackground={true} />
            ))}
          </div>
        </div>
      ))}

      {children}
    </div>
  );
}
